import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import { HousePlus, SquarePen, Send } from 'lucide-react';
import logoLight from "../../assets/images/logo/bidbro-logo-text.png";
import './Header.css';

interface HeaderProps {
  isFullWidth?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isFullWidth = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const params = new URLSearchParams();
    params.set('q', searchQuery);
    navigate(`/search?${params.toString()}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Determine container class based on fullWidth prop
  const containerClass = isFullWidth ? "container-fluid px-4" : "container";

  // Adjust search container width based on fullWidth prop
  const searchContainerClass = `search-container ${isFullWidth ? 'search-wide' : ''}`;

  return (
    <header className={`header fixed-top ${isScrolled ? 'scrolled' : ''}`}>
      <div className={`${containerClass} h-100`}>
        <div className="d-flex align-items-center justify-content-between h-100">
          <Link to="/" className="d-flex align-items-center">
            <img src={logoLight} alt="BidBro" className="header-logo" />
          </Link>

          <div className="d-flex align-items-center">
            {/* Contact */}
            <Link
              id="btnContact"
              to="/contact"
              className="px-1 px-sm-2 px-md-3 py-1 py-md-2 me-1 btn btn-primary rounded text-nowrap"
              style={{ fontSize: '0.8rem' }}
            >
              <Send size={16} className="me-1" /> 
              <span className="d-none d-sm-inline">Contact</span>
              <span className="d-sm-none">Contact</span>
            </Link>

            {/* Add a job */}
            <Link
              id="btnAddJob"
              to="/create-job"
              className="px-1 px-sm-2 px-md-3 py-1 py-md-2 me-1 btn btn-secondary rounded text-nowrap"
              style={{ fontSize: '0.8rem' }}
            >
              <HousePlus size={16} className="me-1" /> 
              <span className="d-none d-sm-inline">Add a Job</span>
              <span className="d-sm-none">Add</span>
            </Link>
            <UncontrolledTooltip
              placement="bottom"
              target="btnAddJob"
              transition={{ timeout: 150 }} >
              Post a new job
            </UncontrolledTooltip>

            {/* Dashboard */}
            <Link
              id="btnJobsDashboard"
              to="/dashboard"
              className="px-1 px-sm-2 px-md-3 py-1 py-md-2 me-1 btn btn-warning rounded text-nowrap"
              style={{ fontSize: '0.8rem' }}
            >
              <SquarePen size={16} className="me-1" /> 
              <span className="d-none d-sm-inline">Dashboard</span>
              <span className="d-sm-none">Dash</span>
            </Link>
            <UncontrolledTooltip
              placement="bottom"
              target="btnJobsDashboard"
              transition={{ timeout: 150 }} >
              Manage Jobs, Hiring, Contracts & More!
            </UncontrolledTooltip>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;